import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { useMe } from '@/lib/me.js';
import { LIMITE_DO_ANEXO, motivoParaRecusarArquivo, tipoDoArquivo } from '@shared/conselheiro.js';

/**
 * Os anexos de uma manhã: relatório, planilha, apresentação. O arquivo vai do
 * navegador direto ao armazenamento privado (URL assinada pelo HackOS); a API
 * do Academy só registra e confere que chegou.
 */

export type Anexo = {
  id: string;
  nome: string;
  tipo: string;
  tamanho: number;
  enviado_por: string | null;
  enviado_por_user_id: string;
  concluido_em: string | null;
};

type Inicio = { arquivo: { id: string }; envio: { url: string; metodo: 'PUT'; cabecalhos: Record<string, string> } };

/** O que o navegador abre (o HackOS só entrega estes para ver; o resto é download). */
const ABRE_NO_NAVEGADOR = /^(audio\/|image\/(png|jpeg|webp|gif)$|application\/pdf$)/;

const tamanho = (n: number) =>
  n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

/** O PUT direto ao S3, com progresso (fetch não informa o andamento do envio). */
function mandarAoArmazenamento(url: string, cabecalhos: Record<string, string>, arquivo: File, progresso: (p: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(cabecalhos)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => e.lengthComputable && progresso(e.loaded / e.total);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`O armazenamento recusou o envio (${xhr.status}).`)));
    xhr.onerror = () => reject(new Error('A conexão caiu durante o envio. Tente de novo.'));
    xhr.send(arquivo);
  });
}

export function Anexos({
  empresaId,
  manhaId,
  anexos,
  disponivel,
  conselheiro,
  podeAnexar = true,
}: {
  empresaId: string;
  manhaId: string;
  anexos: Anexo[];
  disponivel: boolean;
  conselheiro: boolean;
  podeAnexar?: boolean;
}) {
  const qc = useQueryClient();
  const me = useMe();
  const entrada = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState<{ nome: string; p: number } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const recarregar = () => qc.invalidateQueries({ queryKey: ['conselheiro-mes', empresaId] });

  async function enviar(arquivo: File) {
    setErro(null);
    const tipo = tipoDoArquivo(arquivo.name, arquivo.type);
    const recusa = motivoParaRecusarArquivo({ finalidade: 'ANEXO', tipo, tamanho: arquivo.size });
    if (recusa) {
      // Limpa a escolha: o mesmo arquivo escolhido de novo dispara o aviso outra vez.
      if (entrada.current) entrada.current.value = '';
      return setErro(recusa);
    }
    setEnviando({ nome: arquivo.name, p: 0 });
    try {
      const { data } = await api.post<Inicio>(`/conselheiro/empresas/${empresaId}/anexos`, {
        manha_id: manhaId,
        nome: arquivo.name,
        tipo,
        tamanho: arquivo.size,
      });
      await mandarAoArmazenamento(data.envio.url, data.envio.cabecalhos, arquivo, (p) => setEnviando({ nome: arquivo.name, p }));
      await api.post(`/conselheiro/anexos/${data.arquivo.id}/concluir`);
      await recarregar();
    } catch (e) {
      setErro(e instanceof Error && !('response' in e) ? e.message : extractError(e).message);
    } finally {
      setEnviando(null);
      if (entrada.current) entrada.current.value = '';
    }
  }

  async function abrir(a: Anexo, modo: 'ver' | 'baixar') {
    setErro(null);
    // Para ver, a aba abre antes do pedido, senão o navegador a trata como pop-up.
    // Para baixar, a própria página segue o link: o arquivo vem como download e
    // a tela fica onde está.
    const aba = modo === 'ver' ? window.open('', '_blank') : null;
    try {
      const { data } = await api.get<{ url: string }>(`/conselheiro/anexos/${a.id}/link`, { params: { modo } });
      if (aba) aba.location.href = data.url;
      else window.location.href = data.url;
    } catch (e) {
      aba?.close();
      setErro(extractError(e).message);
    }
  }

  async function apagar(a: Anexo) {
    if (!window.confirm(`Apagar "${a.nome}"? Não dá para desfazer.`)) return;
    setErro(null);
    try {
      await api.delete(`/conselheiro/anexos/${a.id}`);
      await recarregar();
    } catch (e) {
      setErro(extractError(e).message);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {anexos.length === 0 ? (
        <p className="text-sm text-ha-subtle">Nenhum anexo nesta manhã.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {anexos.map((a) => (
            <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-2 rounded-card border border-ha-border2 px-3 py-2 text-sm">
              <span className="min-w-0 break-words font-medium text-ha-text">{a.nome}</span>
              <span className="flex flex-wrap items-baseline gap-3 text-xs text-ha-muted">
                <span>
                  {tamanho(a.tamanho)}
                  {a.enviado_por ? ` · ${a.enviado_por}` : ''}
                </span>
                {ABRE_NO_NAVEGADOR.test(a.tipo) ? (
                  <button type="button" className="text-accent underline" onClick={() => abrir(a, 'ver')}>
                    Abrir
                  </button>
                ) : null}
                <button type="button" className="text-accent underline" onClick={() => abrir(a, 'baixar')}>
                  Baixar
                </button>
                {conselheiro || a.enviado_por_user_id === me.data?.id ? (
                  <button type="button" className="text-ha-muted underline" onClick={() => apagar(a)}>
                    Apagar
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}

      {podeAnexar ? (
        disponivel ? (
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={entrada}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.png,.jpg,.jpeg,.webp,.gif,audio/*"
              onChange={(e) => e.target.files?.[0] && enviar(e.target.files[0])}
            />
            <button
              type="button"
              className="rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black disabled:opacity-50"
              style={{ background: 'var(--primary)' }}
              disabled={!!enviando}
              onClick={() => entrada.current?.click()}
            >
              Anexar arquivo
            </button>
            {enviando ? (
              <span className="text-xs text-ha-muted">
                Enviando {enviando.nome}… {Math.round(enviando.p * 100)}%
              </span>
            ) : (
              <span className="text-xs text-ha-muted">PDF, Office, imagem, texto ou áudio, até {LIMITE_DO_ANEXO / 1024 / 1024} MB.</span>
            )}
          </div>
        ) : (
          <p className="text-xs text-ha-muted">Anexos indisponíveis por enquanto: o armazenamento ainda não foi configurado.</p>
        )
      ) : null}
      {erro ? <p className="text-sm text-red-600">{erro}</p> : null}
    </div>
  );
}
